/**
 * ContentToRegenerate — placeholder affiché quand le contenu stocké est dans
 * une autre langue que la langue UI active.
 *
 * Usage :
 *   <ContentToRegenerate
 *     stored="fr"
 *     onRegenerate={() => regenerate.mutate()}
 *     busy={regenerate.isPending}
 *   />
 *
 * Style : carte premium + flag de la langue UI + bouton de régénération mis
 * en avant. Respecte le ton design Network-iA.
 */
import { Sparkles, Loader2, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS, type LangCode } from '@/i18n';

interface Props {
  /** Langue du contenu actuellement stocké. */
  stored: string;
  onRegenerate: () => void;
  busy?: boolean;
  className?: string;
}

export function ContentToRegenerate({ stored, onRegenerate, busy = false, className }: Props) {
  const { t, i18n } = useTranslation();
  const wanted = (i18n.resolvedLanguage as LangCode) ?? 'fr';
  const wantedMeta = SUPPORTED_LANGS.find((l) => l.code === wanted) ?? SUPPORTED_LANGS[0];
  const storedMeta = SUPPORTED_LANGS.find((l) => l.code === stored);

  return (
    <div
      className={
        'card-hi relative overflow-hidden rounded-3xl p-6 sm:p-8 ' + (className ?? '')
      }
    >
      <div
        className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, #a855f7, transparent 70%)' }}
      />
      <div className="relative flex items-start gap-4">
        <span
          className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-[0_10px_22px_-8px_rgba(168,85,247,0.55)]"
          style={{ background: 'linear-gradient(135deg, #3b82f6, #a855f7, #ec4899)' }}
        >
          <Languages size={20} strokeWidth={2.2} />
        </span>
        <div className="flex-1">
          <p className="kicker text-violet">
            <span className="text-lg leading-none">{wantedMeta.flag}</span>
            {t('regen.kicker', { defaultValue: 'Contenu à régénérer' })}
          </p>
          <h3 className="mt-2 font-display text-[clamp(18px,2vw,22px)] font-800 leading-[1.18] tracking-[-0.02em] text-ink">
            {t('regen.title', {
              defaultValue: 'Ce contenu est en {{from}}, votre langue actuelle est {{to}}.',
              from: storedMeta?.label ?? stored,
              to: wantedMeta.label,
            })}
          </h3>
          <p className="mt-2 max-w-xl text-[13.5px] leading-[1.55] text-ink/65">
            {t('regen.subtitle', {
              defaultValue:
                "Cliquez sur Régénérer — Inea va recréer ce module dans votre langue. C'est rapide et le résultat sera adapté à votre positionnement.",
            })}
          </p>
          <button
            onClick={onRegenerate}
            disabled={busy}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-blue via-violet to-pink px-5 py-2.5 text-[13px] font-700 text-white shadow-[0_10px_22px_-8px_rgba(168,85,247,0.55)] transition hover:scale-[1.02] disabled:opacity-60"
          >
            {busy ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Sparkles size={14} />
            )}
            {busy
              ? t('regen.busy', { defaultValue: 'Inea écrit…' })
              : t('regen.cta', { defaultValue: 'Régénérer dans {{lang}}', lang: wantedMeta.label })}
          </button>
        </div>
      </div>
    </div>
  );
}
